import { GridColDef, GridColumnGroupingModel } from "@mui/x-data-grid-premium";
import { Box, Stack } from "@mui/material";
import { Direction } from "../lazertrader/types";
import { formatDeliveryWindow } from "../../deliveryWindow";
import { QuoteCell } from "./quoteCell";
import { SpotCell } from "./spotCell";
import { AREA_CLUSTERS } from "./areaClusters";
import { PRICE_UNIT } from "../../price";
import { QUANTITY_UNIT } from "../../megawatts";
import {
  askField,
  bidField,
  clusterFields,
  spotField,
  WideOrderBookRow,
} from "./hooks";

const QUOTE_WIDTH = 92;
// Narrower than a quote column: spot carries no MW figure beside the price.
const SPOT_WIDTH = 135;
const PRODUCT_WIDTH = 145;

/**
 * The units, as a header tooltip rather than in the header text.
 *
 * Bid, ask and spot repeat for every cluster, so putting the unit in the header
 * would spell the same thing out fifteen times and spend on it width the prices
 * themselves need. `description` renders as a tooltip on header hover.
 */
const QUOTE_DESCRIPTION = `Price in ${PRICE_UNIT}, quantity in ${QUANTITY_UNIT}`;
// Spot is a cleared price with no quantity beside it.
const SPOT_DESCRIPTION = `Price in ${PRICE_UNIT}`;

export const orderBookContentWidth = (hiddenClusters: Set<string>): number => {
  const visible = AREA_CLUSTERS.filter(
    (cluster) => !hiddenClusters.has(cluster.id)
  ).length;
  const clusterWidth = 2 * (QUOTE_WIDTH + 1) + (SPOT_WIDTH + 1);
  return PRODUCT_WIDTH + visible * clusterWidth + 2;
};

export const CLUSTER_START_CLASS = "cluster-start";

/** The delivery-window column. Exported so cell styling can target it by field. */
export const PRODUCT_FIELD = "deliveryWindow";

const clusterColumns: GridColDef[] = AREA_CLUSTERS.flatMap((cluster) => [
  {
    field: bidField(cluster.id),
    headerName: "Best Bid",
    description: QUOTE_DESCRIPTION,
    width: QUOTE_WIDTH,
    align: "center",
    headerAlign: "center",
    cellClassName: CLUSTER_START_CLASS,
    headerClassName: CLUSTER_START_CLASS,
    type: "number",
    renderCell: (params) => (
      <QuoteCell
        row={params.row}
        clusterId={cluster.id}
        direction={Direction.BUY}
      />
    ),
  },
  {
    field: askField(cluster.id),
    headerName: "Best Ask",
    description: QUOTE_DESCRIPTION,
    width: QUOTE_WIDTH,
    align: "center",
    headerAlign: "center",
    type: "number",
    renderCell: (params) => (
      <QuoteCell
        row={params.row}
        clusterId={cluster.id}
        direction={Direction.SELL}
      />
    ),
  },
  {
    field: spotField(cluster.id),
    headerName: "Spot",
    description: SPOT_DESCRIPTION,
    width: SPOT_WIDTH,
    align: "center",
    headerAlign: "center",
    type: "number",
    renderCell: (params) => (
      <SpotCell row={params.row} clusterId={cluster.id} />
    ),
  },
]);

export const columns: GridColDef[] = [
  {
    field: PRODUCT_FIELD,
    headerName: "Product",
    width: PRODUCT_WIDTH,
    align: "left",
    valueGetter: (_value, row: WideOrderBookRow) =>
      formatDeliveryWindow(
        row.contractDeliveryStartUtc,
        row.contractDeliveryEndUtc
      ) ?? row.id,
    renderCell: (params) => (
      <Stack direction={"row"} sx={{ marginLeft: "6px" }}>
        {params.value}
      </Stack>
    ),
  },
  ...clusterColumns,
  {
    field: "contractDeliveryStartUtc",
    headerName: "ContractDeliveryStartUtc",
  },
  {
    field: "contractDeliveryEndUtc",
    headerName: "ContractDeliveryEndUtc",
  },
];

export const columnGroupingModel: GridColumnGroupingModel = AREA_CLUSTERS.map(
  (cluster) => ({
    groupId: cluster.id,
    headerName: cluster.displayName,
    headerClassName: CLUSTER_START_CLASS,
    children: clusterFields(cluster.id).map((field) => ({ field })),
    renderHeaderGroup: () => (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        gap={0.25}
        width="100%"
        sx={{ "&:hover .cluster-hide": { opacity: 1 } }}
      >
        <Box
          component="span"
          sx={{
            fontSize: "1.1em",
            fontWeight: 700,
            letterSpacing: "0.03em",
          }}
        >
          {cluster.displayName}
        </Box>
      </Stack>
    ),
  })
);
